import { TechnicalDetails } from '@beyond-js/ui/react';
import { Refusal } from '@snapshots/transport';
import { Outage } from './outage';

type Text = (key: string, values?: Record<string, string | number>) => string;

/** Whether what failed was a read, where nothing could change, or a change */
export type Attempt = 'read' | 'change';

/**
 * A change that met an outage, in the shape of `@beyond-js/ui`'s `ChangeOutcome` (D186): `unavailable` when
 * the service answered that it could not, `unknown` when it got no answer; `details` is its Technical details
 */
export interface Outcome {
	outcome: 'unavailable' | 'unknown';
	cause: string;
	details: { text: string; request?: string; time: Date; locale: string; labels: (typeof TechnicalDetails.labels)['en'] };
}

/**
 * What a person reads when something did not happen (decision D20): one sentence per cause with its way on,
 * naming the product that did not answer, and "nothing changed" only where something could have. An
 * unreachable service is never worded as a refusal, and a change that got no answer is said to have perhaps
 * been made. A read nobody answered says who did not answer in the words of the page's line (`Outage`,
 * D163). The code stays in "Technical details".
 */
export class Problems {
	static #codes: Record<string, string> = {
		FORBIDDEN: 'problem.forbidden',
		CONFLICT: 'problem.conflict',
		NOT_FOUND: 'problem.missing',
		CAPTURE_DELETED: 'problem.state.deleted',
		CAPTURE_INCOMPLETE: 'problem.incomplete',
		VALIDATION_FAILED: 'problem.invalid',
		DIGEST_MISMATCH: 'problem.invalid',
		SIZE_MISMATCH: 'problem.invalid',
		PAYLOAD_TOO_LARGE: 'problem.large'
	};
	static #bounds: Record<string, string> = { snapshots: 'snapshots', storage: 'storage', captures: 'captures', bytes: 'bytes' };

	/** Anything a call threw, as the refusal it stands for */
	static refusal(cause: unknown): Refusal {
		return cause instanceof Refusal ? cause : new Refusal('UNREACHABLE', String(cause));
	}

	/**
	 * @param attempt Whether a read or a change failed: only a change says that nothing changed
	 * @param specs `size` says a limit in bytes for a person; `enable`, that Packages is off for the project in view
	 */
	static say(text: Text, refusal: Refusal, attempt: Attempt = 'change', specs: { size?: (bytes: number) => string; enable?: boolean } = {}): string {
		if (refusal.uncertain) return text('problem.unknown');
		const cause = Outage.cause(refusal);
		if (cause) return attempt === 'read' ? Outage.title(text, cause) : `${Outage.title(text, cause)}. ${text('problem.unchanged')}`;
		const details = refusal.details ?? {};
		if (refusal.code === 'LIMIT_EXCEEDED') {
			const bound = Problems.#bounds[String(details.bound ?? '')];
			const limit = Number(details.limit);
			const amount = bound === 'storage' || bound === 'bytes' ? (specs.size?.(limit) ?? String(limit)) : String(limit);
			return bound ? text(`problem.limit.${bound}`, { limit: amount }) : text('problem.limit.busy');
		}
		if (refusal.code === 'STATE_CONFLICT') return text(`problem.state.${['active', 'deleted', 'cancelled', 'failed', 'expired'].includes(String(details.state)) ? details.state : 'active'}`);
		if (refusal.code === 'NOT_ADMITTED') return text(specs.enable ? 'problem.admission.enable' : 'problem.admission');
		return text(Problems.#codes[refusal.code] ?? 'problem.refused');
	}

	/**
	 * A change that met an outage, said the family's way (D186), or null for any other answer: who did not
	 * answer, and whether nothing was changed (the service said so) or the change may have been made (no answer)
	 */
	static outcome(text: Text, refusal: Refusal, locale: string): Outcome | null {
		const cause = refusal.uncertain ? 'snapshots' : Outage.cause(refusal);
		if (!cause) return null;
		const details = { ...Problems.details(refusal), locale, labels: TechnicalDetails.labels[locale === 'es' ? 'es' : 'en'] };
		return { outcome: refusal.uncertain ? 'unknown' : 'unavailable', cause: Outage.title(text, cause), details };
	}

	/**
	 * What "Technical details" holds (D43): the code and the service's own words, the request the answer
	 * named and when it came. The sentence a person reads never carries the code itself.
	 */
	static details(refusal: Refusal): { text: string; request?: string; time: Date } {
		const status = refusal.status ? ` (HTTP ${refusal.status})` : '';
		return { text: `${refusal.code}${status}: ${refusal.message}`, request: refusal.request, time: refusal.time };
	}
}
